import { supabase, toError } from '@/lib/supabase'
import type { Product, ProductInsert, ProductUpdate } from '@/types/database'
import { profileService } from './profileService'
import { activityLogService } from './activityLogService'
import { productComponentService } from './productComponentService'

export type ProductWithCreator = Product & {
  creator?: { full_name: string | null } | null
}

export const productService = {
  /**
   * Get all products for the current organization
   */
  async getProducts(): Promise<ProductWithCreator[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('products')
      .select('*, creator:profiles!products_created_by_fkey(full_name)')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching products:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get a single product by ID
   */
  async getProduct(id: string): Promise<ProductWithCreator | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return null

    const { data, error } = await supabase
      .from('products')
      .select('*, creator:profiles!products_created_by_fkey(full_name)')
      .eq('id', id)
      .eq('organization_id', orgId)
      .single()

    if (error) {
      console.error('Error fetching product:', error)
      return null
    }

    return data
  },

  /**
   * Create a new product
   */
  async createProduct(product: Omit<ProductInsert, 'organization_id'>): Promise<Product | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { data: { user } } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from('products')
      .insert({
        ...product,
        organization_id: orgId,
        created_by: user?.id,
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating product:', error)
      throw toError(error)
    }

    if (data) {
      activityLogService.log({
        action: 'created',
        entityType: 'product',
        entityId: data.id,
        entityName: data.name,
      })
    }

    return data
  },

  /**
   * Update a product
   */
  async updateProduct(id: string, updates: ProductUpdate): Promise<Product | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { data, error } = await supabase
      .from('products')
      .update(updates)
      .eq('id', id)
      .eq('organization_id', orgId)
      .select()
      .single()

    if (error) {
      console.error('Error updating product:', error)
      throw toError(error)
    }

    if (data) {
      activityLogService.log({
        action: 'updated',
        entityType: 'product',
        entityId: data.id,
        entityName: data.name,
        details: { fields_changed: Object.keys(updates) },
      })
    }

    return data
  },

  /**
   * Delete a product
   */
  async deleteProduct(id: string): Promise<boolean> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    // Fetch name before deletion for the log
    const existing = await this.getProduct(id)

    const { error } = await supabase
      .from('products')
      .delete()
      .eq('id', id)
      .eq('organization_id', orgId)

    if (error) {
      console.error('Error deleting product:', error)
      throw toError(error)
    }

    activityLogService.log({
      action: 'deleted',
      entityType: 'product',
      entityId: id,
      entityName: existing?.name,
    })

    return true
  },

  /**
   * Get products by status
   */
  async getProductsByStatus(status: Product['status']): Promise<Product[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('organization_id', orgId)
      .eq('status', status)
      .order('name')

    if (error) {
      console.error('Error fetching products by status:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Duplicate a product and its BOM (product_components).
   * Optionally copies document links.
   */
  async duplicateProduct(
    sourceProductId: string,
    options: { newName: string; newSku?: string; copyDocuments?: boolean }
  ): Promise<Product> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const source = await this.getProduct(sourceProductId)
    if (!source) throw new Error('Source product not found')

    const { data: { user } } = await supabase.auth.getUser()

    // 1. Create the new product
    const { data: newProduct, error: createError } = await supabase
      .from('products')
      .insert({
        organization_id: orgId,
        name: options.newName,
        sku: options.newSku || null,
        description: source.description,
        category: source.category,
        location: source.location,
        status: 'draft' as const,
        target_markets: source.target_markets,
        product_factors: source.product_factors,
        metadata: source.metadata,
        created_by: user?.id,
      })
      .select()
      .single()

    if (createError || !newProduct) {
      console.error('Error duplicating product:', createError)
      throw createError ?? new Error('Failed to create duplicate product')
    }

    // 2. Copy BOM rows
    const sourceComponents = await productComponentService.getProductComponents(sourceProductId)
    if (sourceComponents.length > 0) {
      await productComponentService.bulkAddComponentsToProduct(
        newProduct.id,
        sourceComponents.map((pc) => ({
          component_id: pc.component_id,
          quantity: pc.quantity,
          unit: pc.unit || 'pcs',
          reference_designator: pc.reference_designator || undefined,
          assembly_notes: pc.assembly_notes || undefined,
        }))
      )
    }

    // 3. Optionally copy document links
    if (options.copyDocuments) {
      const { data: docs } = await supabase
        .from('documents')
        .select('*')
        .eq('product_id', sourceProductId)
        .eq('organization_id', orgId)

      if (docs && docs.length > 0) {
        const docInserts = docs.map((doc) => ({
          organization_id: orgId,
          product_id: newProduct.id,
          component_id: doc.component_id,
          name: doc.name,
          file_path: doc.file_path,
          file_size: doc.file_size,
          mime_type: doc.mime_type,
          type: doc.type,
          status: doc.status,
          expiry_date: doc.expiry_date,
          tags: doc.tags,
          metadata: doc.metadata,
          uploaded_by: user?.id,
        }))
        const { error: docError } = await supabase.from('documents').insert(docInserts)
        if (docError) {
          console.error('Error copying documents during product duplication:', docError)
          throw docError
        }
      }
    }

    // 4. Log the duplication
    activityLogService.log({
      action: 'duplicated',
      entityType: 'product',
      entityId: newProduct.id,
      entityName: newProduct.name,
      details: {
        source_product_id: sourceProductId,
        source_product_name: source.name,
        components_copied: sourceComponents.length,
        documents_copied: options.copyDocuments ?? false,
      },
    })

    return newProduct
  },

  /**
   * Get product count by status for dashboard
   */
  async getProductStats(): Promise<{ total: number; byStatus: Record<string, number> }> {
    const products = await this.getProducts()

    const byStatus = products.reduce((acc, product) => {
      const status = product.status || 'draft'
      acc[status] = (acc[status] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    return {
      total: products.length,
      byStatus,
    }
  },
}
